import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy, capitalize } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import Download from 'sushiweb/Icons/all/Download';
import Cross from 'sushiweb/Icons/all/Cross';
import SpinnerCircular from 'sushiweb/Spinner/SpinnerCircular';
import {
  exportCSV,
  formatDateTime,
  formatQuestionsColumn,
  getCurrentDate,
  getValueForKey,
  includesOption,
  indexOfOption,
  formatMultiSelectAnswers,
  replaceSpecialCharacters,
  formatDateSorting,
  emailRegex,
  numericRegex,
} from '../../../helpers';
import {
  DownloadIconWrapper,
  SearchContainerV2,
  StatusFilterAndExportCSVContainer,
  StyledSearchInput,
  TableContainer,
  TooltipWrapper,
} from '../../common/styles';
import DataTables from '../../common/components/DataTables/DataTables';
import {
  consumptionDetailsHeadColumns,
  orderExpenseDetailsHeadColumns,
  getInvoiceColumn,
} from '../../common/components/DataTables/components/headColumns';
import ErrorPage from '../../common/components/ErrorPage';
import {
  businessTypeEnum,
  orderServiceTypeEnum,
  orderStatusEnum,
  searchOrdersByEnum,
  statusLabels,
} from '../../common/components/constants';
import StatusFilter from '../../common/components/StatusFilter';
import CustomTablePagination from '../../common/components/DataTables/CustomTablePagination';
import { SearchButton } from '../styles';
import { getOrderByOrderId, getOrdersForDownload } from '../../../api/order';
import { billQuestionKey, genericErrorMsg } from '../../../constants';
import ZTrackingClient from '../../../jumbo/ZTrackingClient';

const Consumptions = ({
  ordersResponse,
  fetchingOrders,
  fetchedOrders,
  lastMonthSelected,
  reqStartDate,
  reqEndDate,
  getConsumptions,
  employeeOrdersData,
  fetchingEmployeeOrders,
  fetchedEmployeeOrders,
  getEmployeeConsumptions,
  showErrorToast,
  selectedBusinessType,
}) => {
  const [consumptionHeads, setConsumptionHeads] = useState([]);
  const [downloadQuestionsHeaders, setDownloadQuestionsHeader] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [searchAPI, onSearchAPIInputChange] = useState('');
  const [canSearchAPI, setCanSearchAPI] = useState(true);
  const [searchBy, setSearchBy] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const { data: consumptionDetails } = ordersResponse;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [pageStack, setPageStack] = useState([]);
  const [errSearchTerm, setErrSearchTerm] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const filterStatusOptions = [
    {
      value: [1],
      label: 'ORDER_PLACED_PAID',
      disabled: false,
    },
    {
      value: [3],
      label: 'ORDER_REFUNDED',
      disabled: false,
    },
    {
      value: [4],
      label: 'ORDER_PARTIALLY_REFUNDED',
      disabled: false,
    },
  ];
  const rowsPerPage = 10;
  const customPaginationRequired =
    tableData.length <= rowsPerPage &&
    searchBy === searchOrdersByEnum.UNSPECIFIED &&
    (pageStack?.length || ordersResponse?.last_evaluated_key?.length);

  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  // const enableCurrentFilters = availableStatuses => {
  //   for (let i = 0; i < filterStatusOptions.length; i += 1) {
  //     for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
  //       if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
  //         filterStatusOptions[i].disabled = false;
  //         break;
  //       } else filterStatusOptions[i].disabled = true;
  //     }
  //   }
  //   setFilterStatusOptions(filterStatusOptions);
  // };
  const createHeadColumns = orders => {
    const questionsMap = {};
    const questionsHeader = [];
    const downloadCSVQuestionsHeader = [];
    orders.forEach(order => {
      const userDefinedInputs = order?.orders_meta_data?.user_defined_inputs;
      if (userDefinedInputs) {
        userDefinedInputs.forEach(val => {
          if (!(val.key in questionsMap)) {
            questionsHeader.push({
              name: (
                <TooltipWrapper>
                  {val?.key?.split(' ')?.length > 5 ? (
                    <Tooltip
                      position='top'
                      label={val.key}
                      appearance='light'
                      size='small'
                      style={{
                        fontSize: '12px',
                        opacity: 0.8,
                        padding: '10px',
                        borderRadius: '10px',
                        maxWidth: '250px',
                      }}
                    >
                      {formatQuestionsColumn(val.key)}
                    </Tooltip>
                  ) : (
                    formatQuestionsColumn(val.key)
                  )}
                </TooltipWrapper>
              ),
              selector: row =>
                getValueForKey(
                  row?.orders_meta_data?.user_defined_inputs,
                  val.key,
                ),
              sortable: true,
              grow: 1,
              minWidth: '200px',
              cell: row => {
                const value = getValueForKey(
                  row?.orders_meta_data?.user_defined_inputs,
                  val.key,
                );
                return (
                  <div>
                    {val.key === billQuestionKey ? (
                      <DownloadIconWrapper>
                        <Download
                          color='grey'
                          onClick={() => {
                            window.open(value, '_blank', 'noopener');
                          }}
                        />
                      </DownloadIconWrapper>
                    ) : (
                      value
                    )}
                  </div>
                );
              },
            });
            downloadCSVQuestionsHeader.push({ key: val.key, value: val.key });
            questionsMap[val.key] = 1;
          }
        });
      }
    });
    setConsumptionHeads(questionsHeader);
    setDownloadQuestionsHeader(downloadCSVQuestionsHeader);
  };

  useEffect(() => {
    handlePageChange(1);
    setPageStack([]);
  }, [lastMonthSelected, selectedBusinessType]);

  useEffect(() => {
    if (reqEndDate && reqStartDate && !searchAPI) {
      getConsumptions({
        businessType: selectedBusinessType,
        startDate: reqStartDate,
        endDate: reqEndDate,
        pageKey: pageStack[pageStack.length - 1] ?? '',
        limitPerPage: rowsPerPage,
        orderStatus: currentFilters
          ?.map(filter => filter.value)
          ?.flat()
          ?.join(','),
      });
    }
    if (searchAPI && searchBy === searchOrdersByEnum.EMPLOYEE_EMAIL) {
      ZTrackingClient.recordClick({
        ui_source: 'DASHBOARD',
        component: 'CONSUMPTION_DETAILS_SEARCH_BY_EMAIL',
        meta_data: { search_term: searchAPI },
      });
      getEmployeeConsumptions({
        businessType: selectedBusinessType,
        emailId: searchAPI,
        emailHash: '',
        startDate: reqStartDate,
        endDate: reqEndDate,
      });
    }
    if (searchAPI && searchBy === searchOrdersByEnum.ORDER_ID) {
      ZTrackingClient.recordClick({
        ui_source: 'DASHBOARD',
        component: 'CONSUMPTION_DETAILS_SEARCH_BY_ORDER_ID',
        meta_data: { search_term: searchAPI },
      });
      getOrderByOrderId(searchAPI)
        .then(res => {
          const { status = false, message = genericErrorMsg } = res;
          if (!status) {
            setLoading(false);
            showErrorToast(message);
            return;
          }
          const orderArray = [];
          orderArray.push(res?.data);
          const start = new Date(reqStartDate);
          const end = new Date(reqEndDate);
          const createdOnDate = new Date(
            orderArray[0]?.created_on?.split(' ')[0],
          );
          if (createdOnDate >= start && createdOnDate <= end) {
            if (currentFilters.length !== 0) {
              const filteredSearchArr = orderArray.filter(filterFunc);
              setTableData(filteredSearchArr);
              setSearchResults(filteredSearchArr);
            } else {
              setTableData(orderArray);
              setSearchResults(orderArray);
            }
            setLoading(false);
          } else {
            if (orderArray.length !== 0) {
              showErrorToast('orders is not within the specified date range');
            } else {
              setTableData(orderArray);
              setSearchResults(orderArray);
            }
            setLoading(false);
          }
          handlePageChange(1);
        })
        .catch(error => {
          showErrorToast(genericErrorMsg);
          setLoading(false);
          console.log(error);
        });
    }
  }, [
    reqStartDate,
    reqEndDate,
    selectedBusinessType,
    pageStack,
    searchBy,
    currentFilters,
  ]);

  useEffect(() => {
    if (!customPaginationRequired) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingOrders && fetchedOrders) {
      if (consumptionDetails) {
        consumptionDetails.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (consumptionDetails.length > 0) {
          createHeadColumns(consumptionDetails);
        }
        setTableData(consumptionDetails);
        setSearchResults(consumptionDetails);
        setCurrentData(consumptionDetails.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingOrders]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  useEffect(() => {
    if (searchAPI?.length > 0) {
      setErrSearchTerm(true);
      if (emailRegex.test(searchAPI)) {
        setErrSearchTerm(false);
      }
      if (numericRegex.test(searchAPI) && searchAPI.length >= 9) {
        setErrSearchTerm(false);
      }
    }
    if (searchAPI?.length === 0) {
      setErrSearchTerm(false);
      setSearchBy(searchOrdersByEnum.UNSPECIFIED);
      setCanSearchAPI(true);
    }
    if (searchBy !== searchOrdersByEnum.UNSPECIFIED) {
      setCanSearchAPI(true);
    }
  }, [searchAPI]);

  useEffect(() => {
    if (!fetchingEmployeeOrders && fetchedEmployeeOrders) {
      if (employeeOrdersData) {
        employeeOrdersData.sort(
          (a, b) =>
            formatDateSorting(b.created_on) - formatDateSorting(a.created_on),
        );
        if (employeeOrdersData.length > 0) {
          createHeadColumns(employeeOrdersData);
        }
        if (currentFilters.length !== 0) {
          const filteredSearchArr = employeeOrdersData.filter(filterFunc);
          setTableData(filteredSearchArr);
          setSearchResults(filteredSearchArr);
          setCurrentData(filteredSearchArr.slice(0, 10));
        } else {
          setTableData(employeeOrdersData);
          setSearchResults(employeeOrdersData);
          setCurrentData(employeeOrdersData.slice(0, 10));
        }
        handlePageChange(1);
      }
      setLoading(false);
    }
  }, [fetchingEmployeeOrders]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSearchButtonClick = () => {
    if (searchAPI) {
      if (emailRegex.test(searchAPI)) {
        setSearchBy(searchOrdersByEnum.EMPLOYEE_EMAIL);
        setCanSearchAPI(false);
        setLoading(true);
      } else if (numericRegex.test(searchAPI) && searchAPI.length >= 9) {
        setSearchBy(searchOrdersByEnum.ORDER_ID);
        setCanSearchAPI(false);
        setLoading(true);
      } else {
        setErrSearchTerm(true);
      }
    }
  };

  const onDownloadButtonClick = () => {
    setDownloading(true);
    if (searchAPI && searchBy !== searchOrdersByEnum.UNSPECIFIED) {
      exportSearchResultCSV(searchResults);
      setDownloading(false);
      return;
    }
    getOrdersForDownload(
      reqStartDate,
      reqEndDate,
      currentFilters
        ?.map(filter => filter.value)
        ?.flat()
        ?.join(','),
      selectedBusinessType,
    )
      .then(res => {
        const { status = false, message = genericErrorMsg } = res;
        if (!status) {
          setDownloading(false);
          showErrorToast(message);
          return;
        }
        exportSearchResultCSV(res?.data);
        setDownloading(false);
      })
      .catch(error => {
        setDownloading(false);
        showErrorToast(genericErrorMsg);
        console.log(error);
      });
  };

  const handlePreviousPage = () => {
    if (pageStack.length > 0) {
      setPageStack(prevStack => {
        const newStack = [...prevStack];
        newStack.pop();
        return newStack;
      });
    }
  };

  const handleNextPage = () => {
    setPageStack(prevStack => {
      const newStack = [...prevStack];
      newStack.push(ordersResponse?.last_evaluated_key);
      return newStack;
    });
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setPageStack([]);
    handlePageChange(1);
    setCurrentFilters(newCurrentFilters);

    const metaData = newCurrentFilters.reduce((acc, filter, index) => {
      acc[`filter_${index}`] = String(filter);
      return acc;
    }, {});

    ZTrackingClient.recordClick({
      ui_source: 'DASHBOARD',
      component: 'CONSUMPTION_FILTERS',
      meta_data: metaData,
    });
  };

  const clearFilters = () => {
    setPageStack([]);
    handlePageChange(1);
    setCurrentFilters([]);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearchButtonClick();
    }
  };

  const handleClearSearchButton = () => {
    setSearchBy(searchOrdersByEnum.UNSPECIFIED);
    setCanSearchAPI(true);
    handlePageChange(1);
    onSearchAPIInputChange('');
  };

  const getFilteredColumns = columns => {
    if (searchBy === searchOrdersByEnum.EMPLOYEE_EMAIL) {
      return columns.filter(column => column.name !== 'Email ID');
    }
    return columns;
  };

  const exportSearchResultCSV = downloadData => {
    const headColumns = [
      { key: 'date_time', value: 'Date & time' },
      { key: 'order_id', value: 'Order ID' },
      { key: 'service_type', value: 'Order Type' },
      { key: 'employee_email', value: 'Email ID' },
      { key: 'restaurant', value: 'Restaurant name' },
      { key: 'city', value: 'City' },
      { key: 'state', value: 'State' },
      { key: 'address', value: 'Address' },
      { key: 'total_order_amount', value: 'Total order value' },
      { key: 'paid_using_limit', value: 'Paid by the corporate' },
      { key: 'paid_by_customer', value: 'Paid by customer' },
      { key: 'item_value', value: 'Item value' },
      { key: 'delivery_charge', value: 'Delivery charge' },
      { key: 'tax', value: 'Taxes' },
      { key: 'platform_fee', value: 'Platform fee' },
      { key: 'discount', value: 'Discounts' },
      { key: 'misc_charges', value: 'Other Charge' },
      { key: 'refund_amount', value: 'Refund to corporate budget' },
      { key: 'customer_refund', value: 'Refund to customer' },
      { key: 'priority_fee', value: 'Priority Fee' },
      { key: 'rider_tip', value: 'Rider Tip' },
      { key: 'fi_donation', value: 'Donation' },
      { key: 'pkg_fee', value: 'Packaging Charges' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    downloadData.forEach(data => {
      let labelledData = { ...data };
      labelledData.limit_order_amount = data.amount;
      labelledData.restaurant = replaceSpecialCharacters(
        data?.orders_meta_data?.restaurant_name,
      );
      labelledData.total_order_amount =
        data?.orders_meta_data?.total_order_value;
      labelledData.paid_using_limit =
        data?.orders_meta_data?.corporate_paid_amount;
      labelledData.paid_by_customer =
        data?.orders_meta_data?.customer_paid_amount;
      labelledData.item_value = data?.orders_meta_data?.item_amount;
      labelledData.delivery_charge = data?.orders_meta_data?.delivery_charge;
      labelledData.tax = data?.orders_meta_data?.taxes;
      labelledData.platform_fee = data?.orders_meta_data?.platform_fee;
      labelledData.state = replaceSpecialCharacters(
        data?.orders_meta_data?.state,
      );
      labelledData.city = replaceSpecialCharacters(
        data?.orders_meta_data?.city,
      );
      labelledData.address = replaceSpecialCharacters(
        data?.orders_meta_data?.address,
      );
      labelledData.pkg_fee = data?.orders_meta_data?.pkg_fee;
      labelledData.fi_donation = data?.orders_meta_data?.fi_donation;
      labelledData.rider_tip = data?.orders_meta_data?.rider_tip;
      labelledData.priority_fee = data?.orders_meta_data?.priority_fee;
      labelledData.discount = data?.orders_meta_data?.total_discounts;
      labelledData.misc_charges = data?.orders_meta_data?.msc_charges;
      labelledData.customer_refund =
        data?.orders_meta_data?.customer_refund_amount;
      labelledData.date_time = formatDateTime(data.created_on);
      labelledData.service_type =
        statusLabels[
          orderServiceTypeEnum[(data?.orders_meta_data?.service_type)]
        ];
      labelledData.status = capitalize(
        statusLabels[orderStatusEnum[data.status]],
      );
      const userDefinedInputs = data?.orders_meta_data?.user_defined_inputs;
      let questionsData = {};
      if (userDefinedInputs)
        userDefinedInputs.forEach(val => {
          const newOues = {
            [val.key]: formatMultiSelectAnswers(val.value, ' |'),
          };
          questionsData = { ...questionsData, ...newOues };
        });
      labelledData = { ...labelledData, ...questionsData };
      statusLabelledData.push(labelledData);
    });
    exportCSV(statusLabelledData, `Consumption details - ${getCurrentDate()}`, [
      ...headColumns,
      ...downloadQuestionsHeaders,
    ]);

    const metaData = currentFilters.reduce((acc, filter, index) => {
      acc[`filter_${index}`] = String(filter);
      return acc;
    }, {});

    ZTrackingClient.recordClick({
      ui_source: 'DASHBOARD',
      component: 'CONSUMPTION_DETAILS_DOWNLOAD',
      meta_data: metaData,
    });
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
    width: '50px',
  };
  return (
    <>
      <SearchContainerV2>
        <div style={{ display: 'flex', width: '100%', maxWidth: '600px' }}>
          <StyledSearchInput
            innerWidth='100%'
            value={searchAPI}
            onChange={onSearchAPIInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsSearchClicked(true)}
            onBlur={() => setIsSearchClicked(false)}
            label={
              isSearchClicked || searchAPI?.length ? (
                'Search by email ID or order ID'
              ) : (
                <span style={{ fontSize: '16px', fontWeight: 300 }}>
                  {' '}
                  Search by email ID or order ID
                  {' '}
                </span>
              )
            }
            error={errSearchTerm}
          />
          <SearchButton
            role='button'
            onClick={
              canSearchAPI ? handleSearchButtonClick : handleClearSearchButton
            }
            disabled={errSearchTerm}
            clear={!canSearchAPI}
          >
            {canSearchAPI ? (
              <Search color='white' size={16} />
            ) : (
              <Cross size={24} />
            )}
          </SearchButton>
        </div>
        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          <Tooltip
            position='top'
            label='Download the consumption details'
            appearance='light'
            size='big'
            style={{
              fontSize: '12px',
              opacity: 0.8,
              marginRight: '10px',
              padding: '10px',
              marginLeft: '-30px',
            }}
          >
            {!downloading ? (
              <Download
                color='grey'
                onClick={onDownloadButtonClick}
                style={downloadCSVStyle}
              />
            ) : (
              <div style={downloadCSVStyle}>
                <SpinnerCircular size='medium' />
              </div>
            )}
          </Tooltip>
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedOrders ? (
          <ErrorPage />
        ) : (
          <DataTables
            data={customPaginationRequired ? tableData : currentData}
            columns={[
              ...getFilteredColumns(consumptionDetailsHeadColumns()),
              ...consumptionHeads,
              ...(selectedBusinessType === businessTypeEnum.BUSINESS_TYPE_DINING
                ? orderExpenseDetailsHeadColumns
                : []),
              ...(selectedBusinessType === businessTypeEnum.BUSINESS_TYPE_ONLINE_ORDERING
                ? [getInvoiceColumn()]
                : []),
            ]}
            loading={fetchingOrders || loading}
            pagination
            handlePageChange={handlePageChange}
            customTablePagination={() => (
              <CustomTablePagination
                currentPage={pageStack?.length + 1}
                keyStack={pageStack}
                rowsPerPage={rowsPerPage}
                onPrevPage={handlePreviousPage}
                onNextPage={handleNextPage}
                canNextPage={ordersResponse?.last_evaluated_key?.length > 0}
                dataLoading={loading}
                currentTotalRows={tableData.length}
              />
            )}
            isCustomPagination={customPaginationRequired}
            totalRows={
              customPaginationRequired ? tableData.length : searchResults.length
            }
            onSort={handleSort}
            emptyStateTitle='No consumption details'
          />
        )}
      </TableContainer>
    </>
  );
};

Consumptions.propTypes = {
  ordersResponse: PropTypes.objectOf(PropTypes.any),
  fetchingOrders: PropTypes.bool,
  fetchedOrders: PropTypes.bool,
  lastMonthSelected: PropTypes.bool.isRequired,
  reqStartDate: PropTypes.string.isRequired,
  reqEndDate: PropTypes.string.isRequired,
  getConsumptions: PropTypes.func,
  employeeOrdersData: PropTypes.arrayOf(PropTypes.objectOf),
  getEmployeeConsumptions: PropTypes.func,
  fetchingEmployeeOrders: PropTypes.bool,
  fetchedEmployeeOrders: PropTypes.bool,
  showErrorToast: PropTypes.func,
  selectedBusinessType: PropTypes.number.isRequired,
};

Consumptions.defaultProps = {
  ordersResponse: {},
  fetchingOrders: true,
  fetchedOrders: true,
  getConsumptions: _noop,
  employeeOrdersData: [],
  getEmployeeConsumptions: _noop,
  fetchingEmployeeOrders: false,
  fetchedEmployeeOrders: false,
  showErrorToast: _noop,
};

export default Consumptions;
